import { useNotification, useServiceHandlers } from '@/core'
import type {
  BaseServiceOptions,
  Pagination,
  TRecordAction,
} from '@/core/types'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { MaybeRef } from 'vue'
import {
  type TCustomer,
  customerSalesHandlers,
  customersServiceHandlers,
  searchCustomerServiceHandlers,
  serviceConfig,
  TCustomers,
} from '../../..'

const { model, customers } = serviceConfig

export const useCustomerQuery = (
  options: BaseServiceOptions<Pagination, TCustomer> & {
    rfc: MaybeRef<string>
  },
) => {
  const { currentPage, enableQuery, filters, modelId, rfc } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TCustomer>({
    model,
    currentPage: currentPage ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const deleteRecord = useMutation<void, AxiosError, TCustomer>(
    (payload) => customersServiceHandlers.deleteRecord(payload),
    {
      onSuccess: (_, sale) => serviceHandlers.deleteRecord(sale),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TCustomer, AxiosError, TCustomer>(
    (payload: TCustomer) =>
      customersServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TCustomer, AxiosError, TCustomer>(
    (payload: TCustomer) => customersServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TCustomer, AxiosError, TRecordAction>(
    (payload: TRecordAction) => customersServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TCustomers, AxiosError>(
    [customers.model, currentPage, filters?.loadRecords],
    () =>
      customerSalesHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
        modelId,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecord = useQuery<TCustomer, AxiosError, TCustomer>(
    ['search-customer', rfc],
    ({ queryKey }) =>
      searchCustomerServiceHandlers.searchCustomer(queryKey[1] as string),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecord,
      retry: false,
      onError: (error) => errorHttpNotification(error),
      onSuccess: (payload) => {
        options.handlers?.onLoadRecord?.(payload)
      },
    },
  )

  return {
    loadRecords,
    addRecord,
    editRecord,
    deleteRecord,
    actionRecord,
    loadRecord,
  }
}
